import React from 'react'
import { Paragraph, XStack, YStack, Button, AccountCard, Text, CardProps, Icon } from '@red-ui/components'
import { getString } from '@vrw/data/src/utils/getString'
import { LinkingJourneyOrigins, ViewingRegion } from '@vrw/data/src/utils'
import { Page, Spinner } from '../../../components'
import { MyAccountBackground } from '../MyAccountBackground'
import { PUBLIC_URL } from '../../../config'
import { useAccounts } from '../../../query/accounts'
import { useRedNavigate } from '@/dataImplementation/useRedNavigate'
import { PATHS } from '@/router/paths'
import { getViewingRegion } from '@/dataImplementation/viewingRegionWeb'
import { useLinkVAA } from '@/hooks/useLinkVAA'
import { useFeatureSelector } from '@vrw/data/src/redux/features/features.selectors'
import { FeatureName } from '@vrw/data/src/redux/features/features.types'
import { handleUDAL } from '@/dataImplementation/auth'
import { useAppSelector } from '@/redux/hooks/hooks'
import { formatPoints } from '@vrw/data/src/utils/formatters'
import { useBiltLinking } from '@/feature/ExternalLoyaltyPartners/hooks/useBiltLinking'

const PartnerAccountDetails = ({
  partnerCode,
  accountLinked,
  hideFooterWhenLinked,
  backgroundUri,
  logoUrl,
  linkAccountsAction,
  partnerUserId,
  partnerUserIdLabel,
  loading,
  ctaId,
  footerComponent,
  ...props
}: {
  partnerCode: string
  accountLinked: boolean
  loading?: boolean
  hideFooterWhenLinked?: boolean
  linkAccountsAction: () => void
  backgroundUri: string
  logoUrl: string
  partnerUserId?: string
  partnerUserIdLabel?: string
  ctaId?: string
  footerComponent?: React.ReactNode
} & CardProps) => (
  <YStack alignItems="center">
    <AccountCard
      isCardLinked={accountLinked}
      backgroundUri={backgroundUri}
      logoUrl={logoUrl}
      partnerName={getString(`account.linkAccounts.${partnerCode}.partnerName`)}
      partnerUserId={partnerUserId}
      partnerUserIdLabel={partnerUserIdLabel}
      message={
        accountLinked
          ? getString(`account.linkAccounts.${partnerCode}.status.linked`)
          : getString(`account.linkAccounts.${partnerCode}.status.unlinked`)
      }
      hideFooter={hideFooterWhenLinked && accountLinked}
      width={345}
      margin={0}
      buttonComponent={
        <Button id={ctaId} size="$5" fontWeight="700" height="$6" width="100%" paddingHorizontal="$0" onPress={linkAccountsAction}>
          {loading ? <Spinner size="small" color="white" /> : getString(`account.linkAccounts.${partnerCode}.button`)}
        </Button>
      }
      footerComponent={footerComponent}
      {...props}
    />
    {!accountLinked && (
      <YStack width={345} style={{ textAlign: 'center' }}>
        <Paragraph size={'$4'} fontWeight={'700'} marginVertical={'$3'}>
          {getString(`account.linkAccounts.${partnerCode}.pending.title`)}
        </Paragraph>
        <Paragraph size={'$4'}>{getString(`account.linkAccounts.${partnerCode}.pending.body`)}</Paragraph>
      </YStack>
    )}
  </YStack>
)

const MIN_BILT_POINTS_TO_TRANSFER = 1000

const LinkAccounts = () => {
  const navigate = useRedNavigate()
  const { data: accounts, isLoading: isLoadingAccounts } = useAccounts()
  const { linkVAA, linkStatus } = useLinkVAA()
  const { authenticate } = useBiltLinking()
  const vaaAccount = accounts?.find((account) => account.partnerCode === 'VAA')
  const syfAccount = accounts?.find((account) => account.partnerCode === 'SYF')
  const biltAccount = accounts?.find((account) => account.partnerCode === 'BILTREWARDS')
  const viewingUS = getViewingRegion() === ViewingRegion.US
  const isUDALEnabled = useAppSelector(useFeatureSelector(FeatureName.UDAL_FLOW))
  const webBiltFlag = useAppSelector(useFeatureSelector(FeatureName.WEB_BILT))
  const biltLogoUrl = `/img/link-bilt-account/bilt-logo-${biltAccount ? 'linked' : 'unlinked'}.png`
  const biltPoints = 12000 // This is a placeholder value. Replace with actual logic to fetch Bilt points.

  return (
    <Page title="Your linked accounts | Virgin Red" testId="page-account-link-accounts">
      <MyAccountBackground>
        <XStack alignItems="flex-start" gap="$4" flexWrap="wrap" $tabletLandscape={{ flexDirection: 'column' }}>
          {viewingUS && (
            <PartnerAccountDetails
              loading={isLoadingAccounts}
              accountLinked={!!syfAccount}
              backgroundUri={`${PUBLIC_URL || ''}/img/link-syf-account/card-background.png`}
              logoUrl={`${PUBLIC_URL || ''}/img/link-syf-account/logo-linked.png`}
              partnerCode="syf"
              hideFooterWhenLinked
              linkAccountsAction={() => (isUDALEnabled ? handleUDAL() : navigate(PATHS.LINK_REWARDS_CREDIT_CARD))}
              ctaId={isUDALEnabled ? 'udal-link-syf-account' : 'link-syf-account'}
            />
          )}
          <PartnerAccountDetails
            loading={isLoadingAccounts || linkStatus === 'loading'}
            accountLinked={!!vaaAccount}
            backgroundUri={`${PUBLIC_URL || ''}/img/link-vaa-account/card-background.jpg`}
            logoUrl={`${PUBLIC_URL || ''}/img/link-vaa-account/vaa-logo.png`}
            partnerCode="vaa"
            linkAccountsAction={() => linkVAA(LinkingJourneyOrigins.ACCOUNT)}
            partnerUserIdLabel={getString('account.linkAccounts.vaa.partnerUserIdName')}
            partnerUserId={vaaAccount?.partnerUserId}
          />
          {viewingUS && webBiltFlag && (
            <YStack>
              <PartnerAccountDetails
                loading={isLoadingAccounts}
                accountLinked={!!biltAccount}
                backgroundUri={`${PUBLIC_URL || ''}/img/link-bilt-account/card-background.png`}
                logoUrl={`${PUBLIC_URL || ''}${biltLogoUrl}`}
                partnerCode="bilt"
                linkAccountsAction={authenticate}
                borderEndEndRadius={biltAccount ? '$0' : '$6'}
                borderEndStartRadius={biltAccount ? '$0' : '$6'}
                footerComponent={
                  <YStack
                    width="100%"
                    justifyContent="center"
                    flex={1}
                    backgroundColor="rgba(255, 255, 255, 0.2)"
                    borderRadius="$4"
                    padding="$2">
                    <Text fontSize="$3" color="$white" textAlign="center">{`${formatPoints({ points: biltPoints })} ${getString(
                      'misc.points'
                    )}`}</Text>
                  </YStack>
                }
              />
              {!!biltAccount && (
                <YStack
                  padding="$2"
                  borderWidth={1}
                  borderColor="$black2"
                  borderEndStartRadius="$5"
                  borderEndEndRadius="$5"
                  backgroundColor="$white">
                  <Text fontWeight="700" fontSize="$5" textAlign="center">
                    {getString('account.linkAccounts.bilt.account.transfer.title')}
                  </Text>
                  <Text fontSize="$4" textAlign="center" marginTop="$1">
                    {getString('account.linkAccounts.bilt.account.transfer.body')}
                  </Text>
                  {biltPoints >= MIN_BILT_POINTS_TO_TRANSFER ? (
                    <Button onPress={() => navigate(PATHS.BILT_TRANSFER)} marginTop="$2" variant="secondary" padding="$3">
                      <Text fontSize="$4" fontWeight="500">
                        {getString('account.linkAccounts.bilt.account.transfer.cta')}
                      </Text>
                    </Button>
                  ) : (
                    <XStack alignItems="center" justifyContent="center" marginTop="$3">
                      <Icon.AlertCircle color="$red6" size="$2" marginRight="$0.5" />
                      <Text color="$red6" fontSize="$3" textAlign="center">
                        {getString('account.linkAccounts.bilt.account.transfer.not.allowed').replace(
                          '{{minPoints}}',
                          formatPoints({ points: MIN_BILT_POINTS_TO_TRANSFER })
                        )}
                      </Text>
                    </XStack>
                  )}
                </YStack>
              )}
            </YStack>
          )}
        </XStack>
      </MyAccountBackground>
    </Page>
  )
}

export { LinkAccounts }
